import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationContext,
  ValidationResult,
} from '../types.ts';
import { chromeArg, chromeArgs } from '../validation.ts';
import {
  errorText,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
} from './shared.ts';

interface ExpectedVoice {
  name: string;
  lang: string;
}

const LOCAL_VOICES: Readonly<Record<string, readonly ExpectedVoice[]>> = {
  en_US: [
    {
      name: 'Microsoft Zira Desktop - English (United States)',
      lang: 'en-US',
    },
  ],
  en_GB: [
    {
      name: 'Microsoft George - English (United Kingdom)',
      lang: 'en-GB',
    },
    {
      name: 'Microsoft Hazel - English (United Kingdom)',
      lang: 'en-GB',
    },
    {
      name: 'Microsoft Susan - English (United Kingdom)',
      lang: 'en-GB',
    },
  ],
  es_ES: [
    { name: 'Microsoft Helena - Spanish (Spain)', lang: 'es-ES' },
    { name: 'Microsoft Laura - Spanish (Spain)', lang: 'es-ES' },
    { name: 'Microsoft Pablo - Spanish (Spain)', lang: 'es-ES' },
    { name: 'Microsoft Cosimo - Italian (Italy)', lang: 'it-IT' },
    { name: 'Microsoft Elsa - Italian (Italy)', lang: 'it-IT' },
  ],
};

const RUSSIAN_VOICES: readonly ExpectedVoice[] = [
  { name: 'Microsoft Irina - Russian (Russia)', lang: 'ru-RU' },
  { name: 'Microsoft Pavel - Russian (Russia)', lang: 'ru-RU' },
];

const RUSSIAN_ODD_SEED_VOICES: readonly ExpectedVoice[] = [
  RUSSIAN_VOICES[0]!,
  { name: 'Microsoft Mark - English (United States)', lang: 'en-US' },
  { name: 'Microsoft Zira - English (United States)', lang: 'en-US' },
  { name: 'Microsoft David - English (United States)', lang: 'en-US' },
  RUSSIAN_VOICES[1]!,
];

async function collect(): Promise<RawData> {
  const synth = window.speechSynthesis;
  let voices = synth.getVoices().filter((voice) => voice.localService);
  if (voices.length == 0) {
    voices = await new Promise<SpeechSynthesisVoice[]>((resolve) => {
      const finish = (loadedVoices: SpeechSynthesisVoice[]): void => {
        synth.removeEventListener('voiceschanged', check);
        clearInterval(pollTimer);
        clearTimeout(timeoutTimer);
        resolve(loadedVoices);
      };

      const check = (): void => {
        const loadedVoices = synth
          .getVoices()
          .filter((voice) => voice.localService);
        if (loadedVoices.length > 0) {
          finish(loadedVoices);
        }
      };

      synth.addEventListener('voiceschanged', check);
      const pollTimer = setInterval(check, 250);
      const timeoutTimer = setTimeout(() => {
        finish(synth.getVoices().filter((voice) => voice.localService));
      }, 10_000);
      check();
    });
  }
  return {
    voices: voices.map((voice) => ({
      name: voice.name,
      lang: voice.lang,
      voiceURI: voice.voiceURI,
      default: voice.default,
      localService: voice.localService,
    })),
  };
}

function isOddUint64(value: string | null): boolean {
  if (value == null || !/^\d+$/.test(value)) {
    return false;
  }
  const seed = BigInt(value);
  return seed <= 0xffffffffffffffffn && seed % 2n == 1n;
}

function expectedVoices(
  context: ValidationContext,
): readonly ExpectedVoice[] | null {
  const args = chromeArgs(context);
  if (!args) {
    return null;
  }
  const lang = chromeArg(args, 'lang');
  if (lang == null) {
    return null;
  }
  if (lang == 'ru_RU') {
    return isOddUint64(chromeArg(args, 'seed'))
      ? RUSSIAN_ODD_SEED_VOICES
      : RUSSIAN_VOICES;
  }
  return LOCAL_VOICES[lang] ?? [];
}

function validateVoices(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const reasons: string[] = [];
  const voices = Array.isArray(rawData.voices)
    ? rawData.voices.filter(isRecord)
    : [];
  if (voices.length == 0) {
    reasons.push('No local voices found');
  }
  if (voices.some((voice) => !String(voice.name) || !String(voice.lang))) {
    reasons.push('A local voice has an empty name or language');
  }
  if (voices.some((voice) => voice.localService != true)) {
    reasons.push('Local voices contain a remote voice');
  }
  if (voices.filter((voice) => voice.default == true).length > 1) {
    reasons.push('More than one local voice is marked as default');
  }
  const expected = expectedVoices(context);
  if (
    expected &&
    (voices.length != expected.length ||
      voices.some((voice, index) => {
        const expectedVoice = expected[index];
        return (
          !expectedVoice ||
          voice.name != expectedVoice.name ||
          voice.lang != expectedVoice.lang ||
          voice.voiceURI != expectedVoice.name ||
          voice.default != (index == 0) ||
          voice.localService != true
        );
      }))
  ) {
    const args = chromeArgs(context) ?? [];
    reasons.push(
      `Local voices do not match --lang=${String(chromeArg(args, 'lang'))} and --seed=${String(chromeArg(args, 'seed'))}`,
    );
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  const voices = Array.isArray(rawData.voices)
    ? rawData.voices.filter(isRecord)
    : [];
  const languages = new Set(
    voices.map((voice) => String(voice.lang)).filter(Boolean),
  ).size;
  return `voices ${voices.length} lang ${languages}`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateVoices(rawData, context);
}

export const localVoicesTest: TestDefinition = {
  id: 'localVoices',
  title: 'Local voices',
  category: 'stableProfile',
  spoofingChromeArgs: ['seed'],
  format,
  run,
  validate,
};
